'use client';

import { FC, MouseEvent, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { getActiveUserSubscriptions } from '@/hotdesk-ui/utils/subscriptionUtils';
import { useSubscriptionAvailability } from '@/hotdesk-ui/contexts/SubscriptionAvailabilityContext';
import styles from './ExploreMembershipOfferBanner.module.scss';
const WORKSPACE_IMAGE_BASE = 'https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/webp';
const WORKSPACE_SLIDE_URLS = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11].map(n => `${WORKSPACE_IMAGE_BASE}/workspace-image-${n}.webp`);
const MEMBERSHIPS_HREF = '/memberships';
const isShortTermOrNoPlan = (priceUnit?: string | null): boolean => {
  if (!priceUnit) return true;
  const units = priceUnit.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  if (!units.length) return true;
  return !units.some(unit => unit === 'month' || unit === 'annual' || unit === 'year');
};
const MarqueeRow: FC<{
  images: string[];
}> = ({
  images
}) => {
  const track = [...images, ...images];
  return <div className={styles.marqueeViewport} aria-hidden data-sentry-component="MarqueeRow" data-sentry-source-file="ExploreMembershipOfferBanner.tsx">
            <div className={`${styles.marqueeTrack} ${styles.marqueeLeft}`}>
                {track.map((src, index) => <div key={`${src}-${index}`} className={styles.thumb}>
                        <Image src={src} alt="" fill className={styles.thumbImg} sizes="240px" unoptimized />
                    </div>)}
            </div>
        </div>;
};
export const ExploreMembershipOfferBannerSkeleton: FC = () => <section className={`${styles.banner} ${styles.bannerSkeleton}`} aria-hidden="true" data-sentry-component="ExploreMembershipOfferBannerSkeleton" data-sentry-source-file="ExploreMembershipOfferBanner.tsx">
        <div className={styles.copy}>
            <span className={`${styles.shimmer} ${styles.skelBadge}`} />
            <span className={`${styles.shimmer} ${styles.skelTitle}`} />
            <span className={`${styles.shimmer} ${styles.skelDesc}`} />
            <span className={`${styles.shimmer} ${styles.skelDescShort}`} />
            <span className={`${styles.shimmer} ${styles.skelCta}`} />
        </div>
        <div className={styles.visual}>
            <div className={styles.skelGallery}>
                {[0, 1, 2, 3].map(i => <span key={i} className={`${styles.shimmer} ${styles.skelThumb}`} />)}
            </div>
        </div>
    </section>;
export interface ExploreMembershipOfferBannerProps {
  /** Explore `frequency` (e.g. `hour`, `day`, `day,hour`, `month,annual`). */
  priceUnit?: string | null;
  onDismiss?: () => void;
}

/**
 * Visibility mirrors Navbar Memberships (`subscriptionLink`):
 * `useSubscriptionAvailability().status === 'available'`
 * (RC kill-switch + locale offerings packages).
 *
 * Extra banner-only gates: short-term explore plan, no active membership,
 * and not dismissed.
 */
const ExploreMembershipOfferBanner: FC<ExploreMembershipOfferBannerProps> = ({
  priceUnit = null,
  onDismiss
}) => {
  const t = useTranslations();
  const router = useRouter();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const hasAnyActiveMembership = getActiveUserSubscriptions(user).length > 0;
  const {
    status: subscriptionAvailabilityStatus
  } = useSubscriptionAvailability();
  const [dismissed, setDismissed] = useState(false);

  // Same gate as Navbar `subscriptionLink`.
  const subscriptionLink = subscriptionAvailabilityStatus === 'available';
  const eligibilityLoading = subscriptionAvailabilityStatus === 'pending';
  const canShowForPlan = isShortTermOrNoPlan(priceUnit);
  const showBanner = !dismissed && subscriptionLink && !hasAnyActiveMembership && canShowForPlan;
  const showSkeleton = !dismissed && eligibilityLoading && !hasAnyActiveMembership && canShowForPlan;
  const goToMemberships = () => {
    router.push(MEMBERSHIPS_HREF);
  };
  const handleDismiss = (e: MouseEvent) => {
    e.stopPropagation();
    setDismissed(true);
    onDismiss?.();
  };
  if (showSkeleton) {
    return <ExploreMembershipOfferBannerSkeleton />;
  }
  if (!showBanner) return null;
  return <section className={styles.banner} role="button" tabIndex={0} aria-labelledby="explore-membership-offer-title" onClick={goToMemberships} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToMemberships();
    }
  }} data-sentry-component="ExploreMembershipOfferBanner" data-sentry-source-file="ExploreMembershipOfferBanner.tsx">
            <button type="button" className={styles.closeBtn} onClick={handleDismiss} aria-label={t('workspace-details.membershipOffer.close')}>
                <X size={14} strokeWidth={2.5} color="#000" data-sentry-element="X" data-sentry-source-file="ExploreMembershipOfferBanner.tsx" />
            </button>

            <span className={styles.copy}>
                <span className={styles.badge}>
                    {t('workspace-details.membershipOffer.badge')}
                </span>
                <span id="explore-membership-offer-title" className={styles.title}>
                    {t('workspace-details.membershipOffer.title')}
                </span>
                <span className={styles.subtitle}>
                    {t('workspace-details.membershipOffer.subtitle')}
                </span>
                <span className={styles.cta}>
                    {t('workspace-details.membershipOffer.cta')}
                </span>
            </span>

            <div className={styles.visual} aria-hidden>
                <div className={styles.gallery} dir="ltr">
                    <MarqueeRow images={WORKSPACE_SLIDE_URLS} data-sentry-element="MarqueeRow" data-sentry-source-file="ExploreMembershipOfferBanner.tsx" />
                </div>
            </div>
        </section>;
};
export default ExploreMembershipOfferBanner;